import { Id } from '@/infra/id'
import { collection } from '@/infra/collection'
import DataLoader from 'dataloader'
import { NotFound, retry } from 'fejl'
import {
  slidingBufferTime,
  filterNonNil,
  withAbortSignal,
} from '@/utils/rx-util'
import { match } from '@/utils/pattern-match-util'
import { Conversation } from './Conversation'
import {
  mapConversationMetaToMeta,
  mapConversationResourceToProps,
} from './conversation-mappers'
import {
  mapContentResourceToContent,
  mapMessageTypeResourceToMessageType,
} from './message-mappers'
import type { MessageApi } from './api/message-api'
import type {
  MessageEventResource,
  MessageResource,
  MessageStatusResource,
} from './api/resources/messages'
import type { PrimeUserFn, PrimeWorkspaceFn } from '@/features/iam'
import type { User } from '@/features/iam/users/User'
import type { MessageStatus } from './message-status'
import type { MessageProps } from './Message'
import { type Message } from './Message'
import { safeParseDate } from '@/utils/date-util'
import { memoizeAsync } from '@/utils/memoize-decorator'
import {
  concatAll,
  concatMap,
  defer,
  filter,
  firstValueFrom,
  ignoreElements,
  map,
  merge,
  type Observable,
  ReplaySubject,
  share,
  startWith,
  Subject,
  switchMap,
  tap,
  timer,
} from 'rxjs'
import type { MessageCreated, MessageEvent } from './message-events'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { runInAction } from 'mobx'
import { isNetworkError } from '@/utils/error-util'
import { groupBy } from 'lodash-es'
import { toStream } from '@/utils/mobx-util'

/**
 * Manages conversations and messages.
 */
export class ConversationStore {
  /**
   * Input subject for the mark-as-read pipeline.
   */
  private readonly _markAsReadInput = new Subject<Message>()

  /**
    Stream of messages that were sent by the current user on this device.
   * @private
   */
  private readonly _sentInput = new Subject<Message>()

  /**
   * Dataloader for conversations by ID.
   */
  private readonly _conversationLoader

  /**
   * Used for batch-requesting online status for users. Element string represents
   * the user ID.
   *
   * @private
   */
  private readonly _fetchOnlineStatusRequests = new Subject<string>()

  /**
   * Pipeline for fetching the online status of multiple users in a batch.
   *
   * @private
   */
  private readonly _fetchOnlineStatus$

  /**
   * Stream of messages that were sent by the current user on this device.
   */
  readonly sent$

  /**
   * Subscribe to Conversation events.
   */
  readonly events$

  /**
   * Stream of new messages, excluding messages sent by the current user.
   */
  readonly incomingMessages$

  /**
   * Collection of conversations.
   */
  readonly conversations = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Conversation(),
  })

  constructor(
    private readonly _api: MessageApi,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _primeUser: PrimeUserFn,
    private readonly _currentUser$: Observable<User>,
  ) {
    this._conversationLoader = new DataLoader<string, Conversation | null>(
      this._fetchConversationsCore.bind(this),
    )

    this._fetchOnlineStatus$ = this._fetchOnlineStatusRequests.pipe(
      // Buffer for 100ms, max 500 elements
      slidingBufferTime({ slide: 100, maxWait: 100, count: 500 }),
      // Perform the actual fetch
      concatMap((ids) => this._api.conversations.isOnline(ids)),
      // Flatten the batch results so we get a stream of individual results.
      concatAll(),
      // Only keep a single subscription to the pipeline ever
      share(),
    )

    this.sent$ = this._sentInput.asObservable()

    this.events$ = this._api.events$.pipe(
      concatMap((e) => this._handleRealtimeEvent(e)),
      filterNonNil(),
      share(),
    )

    this.incomingMessages$ = this.events$.pipe(
      filter(
        (e): e is MessageCreated =>
          e.type === 'MessageCreated' && !e.message.sender.isCurrentUser,
      ),
      share(),
    )

    this.events$.subscribe()
    this._fetchOnlineStatus$.subscribe()
    this._currentUser$
      // When we have a user, fetch the unread counts.
      .pipe(
        switchMap(() =>
          withAbortSignal((signal) => this._fetchUnreadCounts(signal)),
        ),
      )
      .subscribe()

    this._markAsReadInput
      .pipe(
        slidingBufferTime({ slide: 100, maxWait: 500, count: 100 }),
        concatMap((messages) => this._markAsRead(messages)),
        share(),
      )
      .subscribe()
  }

  /**
   * Fetches a conversation by ID.
   *
   * NOTE: This is memoized.
   */
  @memoizeAsync()
  async fetchConversation(id: Id): Promise<Conversation | null> {
    return Id.some(id) ? this._conversationLoader.load(Id.assert(id)) : null
  }

  /**
   * Stream for the online status of a user.
   */
  onlineStatusForUser(userId: Id): Observable<boolean> {
    return defer(() => {
      const id = Id.assert(userId)

      // Schedule a fetch of the online status for the user
      this._fetchOnlineStatusRequests.next(id)

      // Stream the online status for the user based on fetch request responses for the user
      const statusForUser$ = this._fetchOnlineStatus$.pipe(
        filter((status) => status.user_public_id === id),
        map((status) => status.online),
      )

      return merge(
        statusForUser$,
        // While subscribed, we also trigger fetches when the backend notifies us of the user's
        // status changing. We're doing this because the status change event's status can't be relied on
        // to be up to date, so we fetch the latest status when notified of a status change.
        this.events$.pipe(
          filter(
            (e) => e.type === 'ParticipantStatusChanged' && e.userId === id,
          ),
          tap(() => this._fetchOnlineStatusRequests.next(id)),
          ignoreElements(),
        ),
      )
    }).pipe(
      startWith(false),
      share({
        // Use a replay subject to buffer the last value (same as our `shareLatest` operator)
        connector: () => new ReplaySubject(1),
        // Don't reset on error or complete
        resetOnError: false,
        resetOnComplete: false,
        // Reset the stream if there are no subscribers for 5 seconds,
        // so if the observable switches between being observed and unobserved quickly, we won't
        // be refiring fetch requests.
        resetOnRefCountZero: () => timer(5_000),
      }),
    )
  }

  /**
   * Lists messages for a conversation.
   */
  async listMessages(
    conversationId: Id,
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Message>> {
    if (!Id.some(conversationId)) {
      return CursorPaged.empty()
    }

    const conversation = await this.fetchConversation(conversationId)
    if (!conversation) {
      return CursorPaged.empty()
    }

    const response = await this._api.messages.list(
      {
        conversation_id: Id.assert(conversationId),
        include_status: true,
        limit: paging.limit,
        after: paging.after ?? undefined,
      },
      signal,
    )

    const props = response.data.map((resource) =>
      this._mapMessageResourceToProps(resource),
    )
    const messages = conversation.messages.set(props)
    return {
      data: messages,
      next: response.cursor,
      total: response.total,
    }
  }

  /**
   * Sends a text message to a conversation. Returns the message even if it
   * didn't send successfully. This method will not throw; check the
   * {@link Message.sendOperationState} to know whether to resend it.
   */
  async sendTextMessage({
    conversation,
    text,
  }: SendTextMessageOptions): Promise<Message> {
    const message = conversation.prepareNewMessage({
      sender: await firstValueFrom(this._currentUser$),
      text,
    })

    return await this.sendMessage(message)
  }

  /**
   * Sends the specified message. This is idempotent if specifying a message
   * already attempted to send before.
   *
   * @param message
   */
  async sendMessage(message: Message) {
    const conversationId = Id.assert(message.conversationId)
    try {
      message.set({ sendOperationState: 'Sending' })
      const response = await retry(
        async (again) =>
          await this._api.messages
            .sendText(conversationId, {
              cid: message.cid,
              text: message.text,
              source: 'ARPHYX',
            })
            .catch((err) => {
              if (isNetworkError(err)) {
                throw again(err)
              }
              throw err
            }),
        {
          tries: 15,
          maxTimeout: 4_000,
          minTimeout: 500,
        },
      )

      // Map and add the message to the conversation
      const messageProps = this._mapMessageResourceToProps(response)
      message.set({
        ...messageProps,
        sendOperationState: 'None',
      })
      this._sentInput.next(message)
    } catch (err: unknown) {
      // Retrying via the UI is a feature, so we won't throw here.
      console.error('Failed to send message', err)
      message.set({ sendOperationState: 'Failed' })
    }

    return message
  }

  /**
   * Marks the specified message as read.
   *
   * @param message
   */
  markAsRead(message: Message) {
    if (!message.isUnread) {
      return
    }

    this._markAsReadInput.next(message)
  }

  /**
   * Gets the unread count for the given conversation.
   *
   * @param conversationId
   */
  unreadCount(conversationId: Id): Observable<number> {
    return toStream(
      () => this.conversations.get(conversationId)?.unreadCount ?? 0,
      true,
    )
  }

  /**
   * Handle realtime events from the API.
   */
  private async _handleRealtimeEvent(
    event: MessageEventResource,
  ): Promise<MessageEvent | null> {
    switch (event.type) {
      case 'MESSAGE_CREATED': {
        const conversationId = Id(event.payload.conversation_id)
        const conversation = await this.fetchConversation(conversationId)

        if (conversation) {
          const message = conversation.messages.set(
            this._mapMessageResourceToProps(event.payload),
          )

          return {
            type: 'MessageCreated',
            message,
            conversation,
          }
        }

        return null
      }
      case 'MESSAGE_DELIVERED': {
        const conversationId = Id(event.payload.conversation_id)
        const conversation = await this.fetchConversation(conversationId)

        if (conversation) {
          const message = conversation.messages.get(event.payload.cid)
          if (message) {
            // Update message delivery status
            message.markDelivered(event.payload.user_public_id)
          }
        }

        return null
      }
      case 'MESSAGE_READ': {
        const conversationId = Id(event.payload.conversation_id)
        const conversation = await this.fetchConversation(conversationId)

        if (conversation) {
          const message = conversation.messages.get(event.payload.cid)
          if (message) {
            // Update message read status
            message.markRead(event.payload.user_public_id)
          } else {
            // We haven't loaded the message which was read from the server yet,
            // but when we do, it'll be read already, which means it won't
            // decrement the unread count. We'll decrement it here to account for
            // this. We'd likely only have the conversation loaded if we
            // fetched unread counts for it.
            conversation.varyUnreadCount(-1)
          }
        }

        return null
      }
      case 'MESSAGE_DELETED': {
        const conversationId = Id(event.payload.conversation_id)
        const conversation = await this.fetchConversation(conversationId)

        if (conversation) {
          conversation.messages.remove(event.payload.cid)
        }

        return null
      }
      case 'PARTICIPANT_ADDED': {
        const conversationId = Id(event.payload.conversation_id)
        const conversation = await this.fetchConversation(conversationId)

        if (conversation) {
          conversation.participants.set({
            userId: event.payload.user_public_id,
          })
        }

        return null
      }
      case 'PARTICIPANT_REMOVED': {
        const conversationId = Id(event.payload.conversation_id)
        const conversation = await this.fetchConversation(conversationId)

        if (conversation) {
          conversation.participants.remove(event.payload.user_public_id)
        }

        return null
      }
      case 'PARTICIPANT_STATUS_CHANGED': {
        return {
          type: 'ParticipantStatusChanged',
          userId: event.payload.user_public_id,
        }
      }
      default:
        // No-op for other event types
        return null
    }
  }

  /**
   * Fetches conversations by IDs.
   *
   * @param ids
   * @private
   */
  private async _fetchConversationsCore(
    ids: readonly string[],
  ): Promise<(Conversation | null)[]> {
    // Mo `get-many`-style API available, so we need to fetch each conversation
    // individually.
    const resources = await Promise.all(
      ids.map((id) =>
        this._api.conversations.get(id).catch(NotFound.ignore(null)),
      ),
    )

    return this.conversations.set(
      resources.map((resource) =>
        resource
          ? mapConversationResourceToProps(resource, this._primeWorkspace)
          : null,
      ),
    )
  }

  /**
   * Fetches the unread counts for all conversations.
   */
  private async _fetchUnreadCounts(signal?: AbortSignal): Promise<void> {
    const response = await this._api.conversations.getUnreadCounts(signal)

    if (signal?.aborted) {
      return
    }

    runInAction(() => {
      for (const resource of response) {
        // Prime the conversation with the unread count, that way it'll start
        // tracking the unread count correctly.
        const conversation = this.conversations.set({
          id: resource.conversation_id,
          meta: mapConversationMetaToMeta(resource.meta),
        })

        conversation.setUnreadCount(resource.unread_count)
      }
    })
  }

  /**
   * Marks the specified messages as read.
   */
  private async _markAsRead(messages: Message[]) {
    const currentUser = await firstValueFrom(this._currentUser$)
    const groupedByWorkspace = groupBy(
      messages,
      (m) => this.conversations.get(m.conversationId)?.workspace.id,
    )
    for (const [workspaceId, messages] of Object.entries(groupedByWorkspace)) {
      await this._api.messages.markRead({
        workspace_id: workspaceId,
        message_ids: messages.map((m) => Id.assert(m.id)),
      })

      runInAction(() => {
        messages.forEach((m) => m.markRead(currentUser.id))
      })
    }
  }

  /**
   * Maps a message resource to message model props with statuses.
   */
  private _mapMessageResourceToProps(resource: MessageResource): MessageProps {
    return {
      id: Id(resource.id),
      cid: resource.cid,
      conversationId: Id(resource.conversation_id),
      sender: this._primeUser({ id: resource.user_public_id }),
      dateCreated: safeParseDate(resource.date_created),
      dateModified: safeParseDate(resource.date_modified),
      messageType: mapMessageTypeResourceToMessageType(resource.content.type),
      content: mapContentResourceToContent(resource.content),
      recipientStatuses: resource.statuses?.map((status) =>
        this._mapMessageStatusResourceToStatus(status),
      ),
    }
  }

  /**
   * Maps a message status resource to our model status with user information.
   */
  private _mapMessageStatusResourceToStatus(
    resource: MessageStatusResource,
  ): MessageStatus {
    const user = this._primeUser({ id: resource.user_public_id })

    return {
      id: resource.id,
      user,
      deliveryStatus: match(resource.status, {
        READ: 'Read',
        DELIVERED: 'Delivered',
        SENT: 'Sent',
      }),
    }
  }
}

interface SendTextMessageOptions {
  /**
   * Conversation to send the text message in.
   */
  conversation: Conversation
  /**
   * Text to send
   */
  text: string
}
